/**
 * @file errorService.js
 * @description Application error logging service. Persists errors to the error_logs
 *   table and optionally fires a fire-and-forget alert for critical/error severity.
 *   Signed-in callers insert directly; signed-out visitors post to the log-client-error
 *   edge function (error_logs stays closed to anon). Never throws — error logging must
 *   not crash the app.
 *
 *   Two entry points:
 *     captureError(err, context, userId, metadata)  uncaught / boundary errors
 *     logCaughtError(err, context, meta)            failures a flow CAUGHT and showed the
 *                                                   user (one line at the call site)
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/errorService
 */
import { supabase } from "./supabase";

// Edge Function URL for critical/error severity alerting (no JWT required)
const NOTIFY_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-critical-error`;
// Signed-out visitors cannot insert into error_logs (RLS), so their reports go through this
// edge function, which validates, rate limits per IP and inserts with the service role.
const GUEST_LOG_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/log-client-error`;

// 2026-09-18: a Chrome extension that monkey-patches window.fetch broke on
// every Google Tag Manager beacon call and flooded error_logs with 247,473
// rows from one browser session in under two hours (cleaned up after the
// fact). Extension-injected errors are never actionable from our side, so
// drop anything whose stack traces back into one before it reaches the
// network at all.
const EXTENSION_STACK_RE = /\b(chrome|moz|safari|edge)-extension:\/\//i;
// 2026-10-17: in-app browsers (Facebook / Instagram on iOS) inject scripts that throw in the page,
// e.g. "Can't find variable: _AutofillCallbackHandler". Not our code and nothing to fix, but they
// flooded Admin > Logs and buried real errors. Matched on the message, only these known names.
const INJECTED_SCRIPT_NOISE_RE = /\b(_AutofillCallbackHandler|__gCrWeb|instantSearchSDKJSBridgeClearHighlight|_pcmBridgeCallbackHandler)\b/;

// Defense in depth for whatever the next runaway loop looks like (a broken
// retry, a third-party script we can't predict): no single browser session
// should be able to write more than this many rows in this window, however
// it's triggered. Resets naturally on reload — it's a per-session cap, not
// a persisted one.
const RATE_LIMIT_WINDOW_MS = 60_000;
const RATE_LIMIT_MAX = 20;
let rateLimitWindowStart = 0;
let rateLimitCount = 0;
let rateLimitNoticeLogged = false;

/**
 * @description Logs an error to the error_logs table and fires an alert webhook
 *   for critical or error severity. Fails silently.
 * @param {Object} params
 * @param {string} params.message - Error message text (truncated to 500 chars)
 * @param {string} [params.stack] - Error stack trace (truncated to 2000 chars)
 * @param {string} [params.context] - Code context where the error occurred
 * @param {string|null} [params.userId] - Authenticated user ID at time of error
 * @param {Object} [params.metadata] - Additional context metadata
 * @returns {Promise<void>}
 */
export async function logError({ message, stack, context = "app", userId = null, metadata = {} }) {
  if (EXTENSION_STACK_RE.test(stack || "")) {
    return;
  }
  if (INJECTED_SCRIPT_NOISE_RE.test(message || "")) {
    return;
  }

  const now = Date.now();
  if (now - rateLimitWindowStart > RATE_LIMIT_WINDOW_MS) {
    rateLimitWindowStart = now;
    rateLimitCount = 0;
    rateLimitNoticeLogged = false;
  }
  rateLimitCount += 1;
  if (rateLimitCount > RATE_LIMIT_MAX) {
    if (!rateLimitNoticeLogged) {
      rateLimitNoticeLogged = true;
      console.warn(`[ErrorService] Rate limit hit (${RATE_LIMIT_MAX}/${RATE_LIMIT_WINDOW_MS}ms) — suppressing further error reports this window.`);
    }
    return;
  }

  const severity = metadata.severity || "error";

  // Baseline environment context on every row, regardless of what (if anything)
  // the caller passed. Without this, error_logs.metadata was always `{}` —
  // a real production crash (react_boundary, Aug 12-13) had no viewport/UA/URL
  // to work from, only a minified stack with no local source map, which left
  // two separate investigations unable to confirm whether it was mobile- or
  // desktop-specific. Caller-supplied metadata (e.g. componentStack) still
  // wins on key collision via the spread order below.
  const envMetadata = typeof window !== "undefined"
    ? {
        viewportWidth:  window.innerWidth,
        viewportHeight: window.innerHeight,
        userAgent:      typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        path:           window.location?.pathname,
      }
    : {};
  metadata = { ...envMetadata, ...metadata };

  // Both INSERT policies on error_logs require an authenticated caller, so a
  // logged-out visitor's direct write is rejected by RLS (and that rejection does
  // NOT throw: supabase-js returns { error }). The policy deliberately stays closed
  // (an anon-writable table is the shape that made security_events forgeable, and
  // one browser extension once wrote 247k rows), so a signed-out report is posted
  // to the log-client-error edge function instead. It truncates, drops extension
  // stacks and enforces a per-IP plus global cap server-side. Fire-and-forget.
  //
  // The alert path below is unaffected and needs no session.
  let session = null;
  try {
    const { data } = await supabase.auth.getSession();
    session = data?.session || null;
  } catch { /* treat an unreadable session as logged out */ }

  if (session) {
    try {
      const { error } = await supabase.from("error_logs").insert({
        message: String(message).slice(0, 500),
        stack: String(stack || "").slice(0, 2000),
        context,
        user_id: userId || session.user?.id || null,
        metadata,
        severity,
        created_at: new Date().toISOString(),
      });
      // insert() resolves with { error } rather than throwing — check it, or an
      // RLS rejection stays silent the way this one did.
      if (error) console.warn("[ErrorService] DB write rejected:", error.message, "|", message);
    } catch {
      // Silently fail — never let error logging crash the app
      console.warn("[ErrorService] Could not log error to DB:", message);
    }
  } else {
    sendGuestError({ message, stack, context, severity, metadata });
  }

  // Fire-and-forget alert for critical or error severity
  if (severity === "critical" || severity === "error") {
    try {
      fetch(NOTIFY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: String(message).slice(0, 500), context, severity, metadata }),
      }).catch(() => {}); // never await — never throw
    } catch {
      // Silently ignore — alerting must never crash the app
    }
  }
}

/**
 * @description Signed-out path: post the report to the log-client-error edge function.
 *   Fire-and-forget; any failure is swallowed (a guest's logging problem must never reach
 *   the guest). The function re-validates and truncates everything it receives.
 */
function sendGuestError({ message, stack, context, severity, metadata }) {
  try {
    fetch(GUEST_LOG_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: String(message).slice(0, 500),
        stack: String(stack || "").slice(0, 2000),
        context,
        severity,
        url: typeof window !== "undefined" ? window.location?.pathname : undefined,
        meta: metadata,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    console.warn("[ErrorService] guest error (not sent):", context, "|", message);
  }
}

// ── Caught failures ─────────────────────────────────────────────────────────

// Only these metadata keys are ever persisted by logCaughtError. Everything is an id, a step
// name or a status: nothing that identifies a person or carries client or message content.
// Anything else a call site passes is dropped, so a careless `{ email }` cannot reach the table.
const CAUGHT_META_ALLOWLIST = new Set([
  "listingId", "caseId", "caseCode", "applicationId", "inviteId", "threadId", "broadcastId",
  "step", "op", "flow", "stage", "kind", "plan", "tier", "reason", "source",
  "code", "errorCode", "status", "httpStatus", "count", "attempt", "slots", "credits",
]);
const CAUGHT_META_VALUE_MAX = 80;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Error text can echo user input (a Postgres detail line, a validation message). Strip emails. */
export function scrubPII(text) {
  return String(text ?? "").replace(EMAIL_RE, "[email]");
}

/** Reduces call-site metadata to allowlisted, primitive, short values. */
export function sanitizeCaughtMeta(meta) {
  const out = {};
  if (!meta || typeof meta !== "object") return out;
  for (const [k, v] of Object.entries(meta)) {
    if (!CAUGHT_META_ALLOWLIST.has(k) || v == null) continue;
    if (typeof v === "string") out[k] = scrubPII(v).slice(0, CAUGHT_META_VALUE_MAX);
    else if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    else if (typeof v === "boolean") out[k] = v;
  }
  return out;
}

// A flow that retries or autosaves can hit the same failure every few seconds. One row per
// context+message per minute is enough to see it; the rate limit in logError stays the hard cap.
const CAUGHT_DEDUPE_MS = 60_000;
const recentCaught = new Map();
// The same Error object is often logged where it is thrown (a service) and again where it is
// caught (the component). Log each object once.
const loggedObjects = new WeakSet();

/** Test hook: forget the dedupe window. */
export function _resetCaughtDedupe() { recentCaught.clear(); }

/**
 * @description One-line logger for failures a flow CAUGHT and surfaced to the user (a toast,
 *   an inline "could not save" message). Use it for unexpected failures only: network errors,
 *   5xx, DB constraint or RLS errors, edge function failures. Do NOT call it for expected user
 *   errors (validation the UI explains inline, a full plan the user must upgrade, and so on).
 *
 *   Severity is always "error". Respects the extension filter and per-session rate limit in
 *   logError. Metadata is reduced to an allowlist of ids, steps and status codes (see
 *   CAUGHT_META_ALLOWLIST); emails are scrubbed from the message. Never throws.
 * @param {Error|string|{message?:string,code?:string,status?:number}} err - The caught value
 * @param {string} context - Stable snake_case context, e.g. "case_post_failed"
 * @param {Object} [meta] - Small structured detail, e.g. { listingId, step, httpStatus }
 * @returns {void}
 */
export function logCaughtError(err, context, meta = {}) {
  try {
    if (!err) return;
    if (err.name === "AbortError") return;
    if (typeof err === "object") {
      if (loggedObjects.has(err)) return;
      loggedObjects.add(err);
    }
    const message = scrubPII(err?.message || String(err)) || "unknown error";
    const now = Date.now();
    const key = `${context}|${message}`;
    const last = recentCaught.get(key);
    if (last != null && now - last < CAUGHT_DEDUPE_MS) return;
    recentCaught.set(key, now);
    if (recentCaught.size > 100) recentCaught.delete(recentCaught.keys().next().value);

    const metadata = {
      ...sanitizeCaughtMeta({
        errorCode: err.code,
        httpStatus: err.status ?? err.context?.status,
      }),
      ...sanitizeCaughtMeta(meta),
      severity: "error",
    };
    logError({ message, stack: scrubPII(err?.stack || ""), context, userId: null, metadata }).catch(() => {});
  } catch { /* logging must never throw */ }
}

/**
 * @description Convenience wrapper for logError — extracts message and stack from an
 *   Error object or string and passes them to logError.
 * @param {Error|string} error - The error to capture
 * @param {string} [context] - Code context where the error occurred
 * @param {string|null} [userId] - Authenticated user ID at time of error
 * @param {Object} [metadata] - Additional context (e.g. React's componentStack)
 * @returns {void}
 */
export function captureError(error, context = "app", userId = null, metadata = {}) {
  const message = error?.message || String(error);
  const stack = error?.stack || "";
  if (isThirdPartyNoise(message)) return;
  logError({ message, stack, context, userId, metadata });
}

/**
 * Errors thrown by other companies' scripts running on our pages, never by our code, so they don't belong in
 * Admin > Logs. Exact signatures only, so a real error is never swallowed.
 *   - "Object Not Found Matching Id:3, MethodName:update, ParamCount:4": Microsoft's link scanners and page renderer
 *     (Outlook Safe Links, Bing). Seen 80 times on 2026-10-21, all logged-out visits to /, in bursts of 20.
 */
const THIRD_PARTY_NOISE = [/^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/];

/** True when an error message is a known third-party script error (see THIRD_PARTY_NOISE). */
export function isThirdPartyNoise(message) {
  const m = String(message || "").trim();
  return THIRD_PARTY_NOISE.some((re) => re.test(m));
}
